import Search from "components/PageView/PageViewTable/Search";
import { atom, useAtom } from "jotai";
import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardHeader, Col, Row } from "reactstrap";
import { AppConfirm } from "utils/PopupUtil";
import OptionsGroup from "./OptionsGroup";
import SelectableOption from "./SelectableOption";

const scrollAtom = atom("scrollAtom");

const SelectableOptionsMenu = ({
  field,
  groupBy,
  nestedGroupBy,
  options,
  multiselect,
  value,
  listDisplayMode = "list", // list|grid
  onSelect,
  onDeselect,
  labelKey,
  colorKey,
  valueKey,
  addableGroups,
  clearable,
  stopEditing,
  setAttemptClear,
  onClear,
  searchable,
}) => {
  const [scrollPosition, setScrollPosition] = useAtom(scrollAtom);
  const [displayedOptions, setDisplayedOptions] = useState(options);

  const menuRef = useRef();

  const handleScroll = () => {
    const position = menuRef?.current?.scrollTop;

    setScrollPosition(position);
  };

  useLayoutEffect(() => {
    if (scrollPosition && menuRef?.current) {
      menuRef.current.scrollTop = scrollPosition;
    }
  }, [value]);

  const handleSearch = (searchedValue) => {
    if (!searchedValue) {
      setDisplayedOptions(options);
    }

    searchedValue = searchedValue.toLowerCase();

    if (groupBy && options?.[0]?.options) {
      const copiedOptions = structuredClone(options);

      setDisplayedOptions(
        copiedOptions.flatMap((item) => {
          const result = [];

          for (const groupKey of Object.keys(item?.options)) {
            let nestedGroup = item?.options?.[groupKey];

            if (!Array.isArray(nestedGroup)) return [];

            nestedGroup = nestedGroup.flatMap((item) => {
              const valueToCheck = item?.[labelKey];

              if (!valueToCheck) return [];

              if (valueToCheck.toLowerCase().includes(searchedValue)) {
                return item;
              } else {
                return [];
              }
            });

            item.options[groupKey] = nestedGroup;

            if (nestedGroup?.length > 0) {
              result.push(item);
            }
          }

          return result;
        })
      );

      return;
    }

    setDisplayedOptions(
      options.filter((item) =>
        item[labelKey].toLowerCase().includes(searchedValue.toLowerCase())
      )
    );
  };

  const handleClear = async (e) => {
    setAttemptClear(true);

    const result = await AppConfirm({
      title: multiselect ? "Clear Selected Options" : "Clear Selected Option",
      html: `Are you sure you want to clear ${
        multiselect ? "all the selected options" : "the selected option"
      } from ${field?.Name} on this record?`,
      confirmButtonText: "Clear",
    });

    setAttemptClear(false);

    if (!result.isConfirmed) {
      e.preventDefault();
      e.stopPropagation();

      return;
    }

    if (typeof onClear === "function") {
      stopEditing();
      onClear();
    }
  };

  const optionsList = useMemo(() => {
    return (
      <>
        {groupBy ? (
          <>
            {displayedOptions.length ? (
              <>
                {displayedOptions?.map((group, i) => (
                  <div
                    key={i}
                    className={`px-2 ${i === 0 && "pt-2"} ${
                      i === displayedOptions?.length - 1 && "pb-1"
                    } ${i !== displayedOptions?.length - 1 && "mb-2"}`}
                  >
                    <OptionsGroup
                      group={group}
                      nestedGroupBy={nestedGroupBy}
                      listDisplayMode={listDisplayMode}
                      value={value}
                      onSelect={onSelect}
                      onDeselect={onDeselect}
                      multiselect={multiselect}
                      labelKey={labelKey}
                      valueKey={valueKey}
                      colorKey={colorKey}
                      addAction={
                        addableGroups ? addableGroups?.[group?.group] : null
                      }
                    />
                  </div>
                ))}
              </>
            ) : (
              <div className="m-2 p-2 bg-ultralight border border-superlight text-dark font-weight-500 rounded-sm">
                No options found.
              </div>
            )}
          </>
        ) : (
          <>
            {displayedOptions?.length ? (
              <>
                {displayedOptions?.map((option, i) => {
                  return (
                    <div key={i} className="m-2">
                      <SelectableOption
                        option={option}
                        value={value}
                        multiselect={multiselect}
                        onSelect={onSelect}
                        onDeselect={onDeselect}
                        labelKey={labelKey}
                        valueKey={valueKey}
                        colorKey={colorKey}
                      />
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="m-2 p-2 bg-ultralight border border-superlight text-dark font-weight-500 rounded-sm">
                No options found.
              </div>
            )}
          </>
        )}
      </>
    );
  }, [
    groupBy,
    listDisplayMode,
    multiselect,
    onSelect,
    onDeselect,
    value,
    displayedOptions,
    addableGroups,
    colorKey,
    labelKey,
    nestedGroupBy,
    valueKey,
  ]);

  return (
    <Card>
      <CardHeader className="p-2 border-bottom border-superlight">
        <Row className="align-items-center">
          <Col xs="">
            <h5 className="m-0 text-dark">
              {field?.Name ? field?.Name : "Edit"}
            </h5>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              className="mr-1"
              outline
              color="danger"
              disabled={
                (!value?.length && multiselect) ||
                (!value && !multiselect) ||
                !clearable
              }
              onClick={handleClear}
            >
              Clear{multiselect && " All"}
            </Button>
            {multiselect && (
              <Button
                size="sm"
                outline
                color="secondary"
                onClick={(e) => {
                  e.preventDefault();

                  stopEditing();
                }}
              >
                Done
              </Button>
            )}
          </Col>
        </Row>
      </CardHeader>
      {searchable && (
        <CardHeader className="px-2 py-2">
          <Search
            onChange={handleSearch}
            alwaysOpen
            block={true}
            searchTime={0}
          />
        </CardHeader>
      )}
      <div
        onScroll={handleScroll}
        ref={menuRef}
        style={{ maxHeight: 350, overflowX: "hidden", overflowY: "auto" }}
      >
        {optionsList}
      </div>
    </Card>
  );
};

export default SelectableOptionsMenu;
